import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Plus, Save, Trash2, Eye, Edit2, GripVertical,
  Image as ImageIcon, Type, Columns, Layout, Grid3X3, X, Upload, Check,
  Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, Maximize2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

// Section Template Definitions with enhanced styling options
const SECTION_TEMPLATES = [
  {
    id: 'text-image-right',
    name: 'Text + Bild rechts',
    description: 'Überschrift und Text links, Bild rechts',
    icon: Layout,
    category: 'content',
    fields: [
      { name: 'title', label: 'Überschrift', type: 'richtext', default: 'Ihre Überschrift' },
      { name: 'titleColor', label: 'Überschrift-Farbe', type: 'color', default: '#1F2937' },
      { name: 'titleBold', label: 'Fett', type: 'toggle', default: true },
      { name: 'titleItalic', label: 'Kursiv', type: 'toggle', default: false },
      { name: 'text', label: 'Text', type: 'textarea', default: 'Beschreiben Sie hier Ihren Inhalt...' },
      { name: 'textColor', label: 'Text-Farbe', type: 'color', default: '#4B5563' },
      { name: 'image', label: 'Bild', type: 'image', default: '' },
      { name: 'imageAlt', label: 'Bild-Beschreibung', type: 'text', default: '' },
      { name: 'buttonText', label: 'Button Text (optional)', type: 'text', default: '' },
      { name: 'buttonLink', label: 'Button Link', type: 'text', default: '' },
      { name: 'buttonColor', label: 'Button-Farbe', type: 'color', default: '#8B7355' },
      { name: 'backgroundColor', label: 'Hintergrundfarbe', type: 'color', default: '#F5F0EB' },
      { name: 'padding', label: 'Innenabstand', type: 'select', options: ['klein', 'mittel', 'groß'], default: 'mittel' }
    ]
  },
  {
    id: 'image-top-text',
    name: 'Bild oben + Text',
    description: 'Großes Bild oben, darunter Überschrift und Text',
    icon: ImageIcon,
    category: 'content',
    fields: [
      { name: 'image', label: 'Bild', type: 'image', default: '' },
      { name: 'imageAlt', label: 'Bild-Beschreibung', type: 'text', default: '' },
      { name: 'imageHeight', label: 'Bild-Höhe', type: 'select', options: ['klein', 'mittel', 'groß'], default: 'mittel' },
      { name: 'title', label: 'Überschrift', type: 'richtext', default: 'Ihre Überschrift' },
      { name: 'titleColor', label: 'Überschrift-Farbe', type: 'color', default: '#1F2937' },
      { name: 'titleBold', label: 'Überschrift Fett', type: 'toggle', default: true },
      { name: 'text', label: 'Text', type: 'textarea', default: 'Beschreiben Sie hier Ihren Inhalt...' },
      { name: 'textColor', label: 'Text-Farbe', type: 'color', default: '#4B5563' },
      { name: 'textAlign', label: 'Ausrichtung', type: 'align', default: 'center' },
      { name: 'backgroundColor', label: 'Hintergrundfarbe', type: 'color', default: '#FFFFFF' }
    ]
  },
  {
    id: 'three-columns-icons',
    name: '3-Spalten mit Icons',
    description: 'Drei Spalten mit Icon, Überschrift und Text - ideal für Features',
    icon: Columns,
    category: 'features',
    fields: [
      { name: 'sectionTitle', label: 'Abschnitts-Überschrift', type: 'richtext', default: 'Unsere Vorteile' },
      { name: 'sectionTitleColor', label: 'Überschrift-Farbe', type: 'color', default: '#1F2937' },
      { name: 'column1Icon', label: 'Icon 1 (Emoji)', type: 'text', default: '🎁' },
      { name: 'column1Title', label: 'Titel 1', type: 'text', default: 'Vorteil 1' },
      { name: 'column1Text', label: 'Text 1', type: 'textarea', default: 'Beschreibung...' },
      { name: 'column2Icon', label: 'Icon 2 (Emoji)', type: 'text', default: '💝' },
      { name: 'column2Title', label: 'Titel 2', type: 'text', default: 'Vorteil 2' },
      { name: 'column2Text', label: 'Text 2', type: 'textarea', default: 'Beschreibung...' },
      { name: 'column3Icon', label: 'Icon 3 (Emoji)', type: 'text', default: '✨' },
      { name: 'column3Title', label: 'Titel 3', type: 'text', default: 'Vorteil 3' },
      { name: 'column3Text', label: 'Text 3', type: 'textarea', default: 'Beschreibung...' },
      { name: 'columnTitleColor', label: 'Spalten-Titel-Farbe', type: 'color', default: '#1F2937' },
      { name: 'columnTextColor', label: 'Spalten-Text-Farbe', type: 'color', default: '#6B7280' },
      { name: 'backgroundColor', label: 'Hintergrundfarbe', type: 'color', default: '#F5F0EB' }
    ]
  },
  {
    id: 'full-width-banner',
    name: 'Volle Breite Banner',
    description: 'Hintergrundbild mit Text-Overlay',
    icon: Layout,
    category: 'hero',
    fields: [
      { name: 'backgroundImage', label: 'Hintergrundbild', type: 'image', default: '' },
      { name: 'overlayColor', label: 'Overlay-Farbe', type: 'color', default: 'rgba(0,0,0,0.5)' },
      { name: 'title', label: 'Überschrift', type: 'richtext', default: 'Willkommen' },
      { name: 'titleBold', label: 'Überschrift Fett', type: 'toggle', default: true },
      { name: 'subtitle', label: 'Untertitel', type: 'text', default: 'Entdecken Sie unsere Kollektion' },
      { name: 'buttonText', label: 'Button Text', type: 'text', default: 'Jetzt entdecken' },
      { name: 'buttonLink', label: 'Button Link', type: 'text', default: '/products' },
      { name: 'buttonColor', label: 'Button-Farbe', type: 'color', default: '#8B7355' },
      { name: 'textColor', label: 'Textfarbe', type: 'color', default: '#FFFFFF' },
      { name: 'height', label: 'Höhe', type: 'select', options: ['klein', 'mittel', 'groß'], default: 'mittel' },
      { name: 'textAlign', label: 'Ausrichtung', type: 'align', default: 'center' }
    ]
  },
  {
    id: 'gallery',
    name: 'Galerie',
    description: 'Mehrere Bilder in einem Raster',
    icon: Grid3X3,
    category: 'gallery',
    fields: [
      { name: 'sectionTitle', label: 'Abschnitts-Überschrift', type: 'richtext', default: 'Galerie' },
      { name: 'sectionTitleColor', label: 'Überschrift-Farbe', type: 'color', default: '#1F2937' },
      { name: 'columns', label: 'Spalten', type: 'select', options: ['2', '3', '4'], default: '3' },
      { name: 'gap', label: 'Abstand', type: 'select', options: ['klein', 'mittel', 'groß'], default: 'mittel' },
      { name: 'image1', label: 'Bild 1', type: 'image', default: '' },
      { name: 'image2', label: 'Bild 2', type: 'image', default: '' },
      { name: 'image3', label: 'Bild 3', type: 'image', default: '' },
      { name: 'image4', label: 'Bild 4', type: 'image', default: '' },
      { name: 'image5', label: 'Bild 5', type: 'image', default: '' },
      { name: 'image6', label: 'Bild 6', type: 'image', default: '' },
      { name: 'rounded', label: 'Abgerundete Ecken', type: 'toggle', default: true },
      { name: 'backgroundColor', label: 'Hintergrundfarbe', type: 'color', default: '#FFFFFF' }
    ]
  }
];

const SectionTemplates = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewSection, setPreviewSection] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [draggedItem, setDraggedItem] = useState(null);

  useEffect(() => {
    loadSections();
  }, []);

  const loadSections = async () => {
    try {
      const response = await axiosInstance.get('/custom-sections');
      setSections(response.data.sections || []);
    } catch (error) {
      console.error('Error loading sections:', error);
      setSections([]);
    } finally {
      setLoading(false);
    }
  };

  const saveSections = async (newSections) => {
    setSaving(true);
    try {
      await axiosInstance.put('/custom-sections', { sections: newSections });
      toast.success('Abschnitte gespeichert!');
    } catch (error) {
      console.error('Error saving sections:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const openTemplateDialog = () => {
    setShowTemplateDialog(true);
  };

  const selectTemplate = (template) => {
    setSelectedTemplate(template);
    const initialData = {};
    template.fields.forEach(field => {
      initialData[field.name] = field.default;
    });
    setEditFormData(initialData);
    setShowTemplateDialog(false);
    setShowEditDialog(true);
  };

  const addSection = () => {
    if (!selectedTemplate) return;

    const newSection = {
      id: `section-${Date.now()}`,
      templateId: selectedTemplate.id,
      templateName: selectedTemplate.name,
      data: editFormData,
      isActive: true,
      order: sections.length,
      createdAt: new Date().toISOString()
    };

    const newSections = [...sections, newSection];
    setSections(newSections);
    saveSections(newSections);
    setShowEditDialog(false);
    setSelectedTemplate(null);
    setEditFormData({});
    toast.success('Abschnitt hinzugefügt!');
  };

  const editSection = (section) => {
    const template = SECTION_TEMPLATES.find(t => t.id === section.templateId);
    if (!template) {
      toast.error('Vorlage nicht gefunden');
      return;
    }
    setSelectedTemplate(template);
    setEditingSection(section);
    setEditFormData({ ...section.data });
    setShowEditDialog(true);
  };

  const updateSection = () => {
    if (!editingSection) return;

    const newSections = sections.map(s =>
      s.id === editingSection.id
        ? { ...s, data: editFormData, updatedAt: new Date().toISOString() }
        : s
    );
    setSections(newSections);
    saveSections(newSections);
    setShowEditDialog(false);
    setEditingSection(null);
    setEditFormData({});
    toast.success('Abschnitt aktualisiert!');
  };

  const deleteSection = (sectionId) => {
    if (!window.confirm('Diesen Abschnitt wirklich löschen?')) return;

    const newSections = sections.filter(s => s.id !== sectionId);
    setSections(newSections);
    saveSections(newSections);
    toast.success('Abschnitt gelöscht!');
  };

  const toggleSection = (sectionId) => {
    const newSections = sections.map(s =>
      s.id === sectionId ? { ...s, isActive: !s.isActive } : s
    );
    setSections(newSections);
    saveSections(newSections);
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;

    const newSections = [...sections];
    const draggedSection = newSections[draggedItem];
    newSections.splice(draggedItem, 1);
    newSections.splice(index, 0, draggedSection);
    newSections.forEach((section, idx) => {
      section.order = idx;
    });

    setSections(newSections);
    setDraggedItem(index);
  };

  const handleDragEnd = () => {
    if (draggedItem !== null) {
      saveSections(sections);
    }
    setDraggedItem(null);
  };

  const updateFormField = (fieldName, value) => {
    setEditFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleImageUpload = async (fieldName, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axiosInstance.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      updateFormField(fieldName, response.data.url);
      toast.success('Bild hochgeladen!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Fehler beim Hochladen');
    }
  };

  const openPreview = (section) => {
    setPreviewSection(section);
    setShowPreviewDialog(true);
  };

  // Render form field based on type
  const renderField = (field) => {
    const value = editFormData[field.name] ?? field.default;

    switch (field.type) {
      case 'text':
      case 'richtext':
        return (
          <div key={field.name} className="space-y-2">
            <Label htmlFor={field.name}>{field.label}</Label>
            <Input
              id={field.name}
              value={value}
              onChange={(e) => updateFormField(field.name, e.target.value)}
              placeholder={field.default}
            />
          </div>
        );

      case 'textarea':
        return (
          <div key={field.name} className="space-y-2">
            <Label htmlFor={field.name}>{field.label}</Label>
            <Textarea
              id={field.name}
              value={value}
              onChange={(e) => updateFormField(field.name, e.target.value)}
              placeholder={field.default}
              rows={3}
            />
          </div>
        );

      case 'toggle':
        return (
          <div key={field.name} className="flex items-center gap-2">
            <input
              type="checkbox"
              id={field.name}
              checked={value}
              onChange={(e) => updateFormField(field.name, e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <Label htmlFor={field.name} className="cursor-pointer">{field.label}</Label>
          </div>
        );

      case 'align':
        return (
          <div key={field.name} className="space-y-2">
            <Label>{field.label}</Label>
            <div className="flex gap-1">
              <Button
                type="button"
                variant={value === 'left' ? 'default' : 'outline'}
                size="sm"
                onClick={() => updateFormField(field.name, 'left')}
              >
                <AlignLeft className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant={value === 'center' ? 'default' : 'outline'}
                size="sm"
                onClick={() => updateFormField(field.name, 'center')}
              >
                <AlignCenter className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant={value === 'right' ? 'default' : 'outline'}
                size="sm"
                onClick={() => updateFormField(field.name, 'right')}
              >
                <AlignRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      case 'image':
        return (
          <div key={field.name} className="space-y-2">
            <Label>{field.label}</Label>
            <div className="flex gap-2 items-center">
              <Input
                value={value}
                onChange={(e) => updateFormField(field.name, e.target.value)}
                placeholder="Bild-URL oder hochladen"
                className="flex-1"
              />
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      handleImageUpload(field.name, e.target.files[0]);
                    }
                  }}
                />
                <Button type="button" variant="outline" size="icon" asChild>
                  <span><Upload className="h-4 w-4" /></span>
                </Button>
              </label>
            </div>
            {value && (
              <div className="mt-2 relative">
                <img
                  src={value}
                  alt="Vorschau"
                  className="w-full max-h-32 object-cover rounded-lg border"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2 h-6 w-6"
                  onClick={() => updateFormField(field.name, '')}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            )}
          </div>
        );

      case 'color':
        return (
          <div key={field.name} className="space-y-2">
            <Label>{field.label}</Label>
            <div className="flex gap-2">
              <input
                type="color"
                value={value?.startsWith('rgba') ? '#000000' : (value || field.default)}
                onChange={(e) => updateFormField(field.name, e.target.value)}
                className="h-10 w-14 rounded border cursor-pointer"
              />
              <Input
                value={value || field.default}
                onChange={(e) => updateFormField(field.name, e.target.value)}
                className="flex-1 font-mono text-sm"
              />
            </div>
          </div>
        );

      case 'select':
        return (
          <div key={field.name} className="space-y-2">
            <Label>{field.label}</Label>
            <select
              value={value || field.default}
              onChange={(e) => updateFormField(field.name, e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white"
            >
              {field.options.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        );

      default:
        return null;
    }
  };

  // Get padding value
  const getPadding = (padding) => {
    switch(padding) {
      case 'klein': return '1rem';
      case 'groß': return '3rem';
      default: return '2rem';
    }
  };

  // Get height value
  const getHeight = (height) => {
    switch(height) {
      case 'klein': return '200px';
      case 'groß': return '500px';
      default: return '350px';
    }
  };

  // Get gap value
  const getGap = (gap) => {
    switch(gap) {
      case 'klein': return '0.5rem';
      case 'groß': return '1.5rem';
      default: return '1rem';
    }
  };

  // Render full preview for a section
  const renderFullPreview = (section) => {
    const data = section.data || {};
    const template = SECTION_TEMPLATES.find(t => t.id === section.templateId);

    switch (section.templateId) {
      case 'text-image-right':
        return (
          <div 
            className="flex flex-col md:flex-row gap-8 items-center"
            style={{ 
              backgroundColor: data.backgroundColor || '#F5F0EB',
              padding: getPadding(data.padding)
            }}
          >
            <div className="flex-1">
              <h2 
                style={{ 
                  color: data.titleColor || '#1F2937',
                  fontWeight: data.titleBold ? 'bold' : 'normal',
                  fontStyle: data.titleItalic ? 'italic' : 'normal'
                }}
                className="text-2xl md:text-3xl mb-4"
              >
                {data.title || 'Überschrift'}
              </h2>
              <p style={{ color: data.textColor || '#4B5563' }} className="text-base leading-relaxed">
                {data.text || 'Ihr Text hier...'}
              </p>
              {data.buttonText && (
                <Button 
                  className="mt-4"
                  style={{ backgroundColor: data.buttonColor || '#8B7355' }}
                >
                  {data.buttonText}
                </Button>
              )}
            </div>
            <div className="flex-1">
              {data.image ? (
                <img src={data.image} alt={data.imageAlt || ''} className="w-full rounded-lg shadow-lg" />
              ) : (
                <div className="w-full h-64 bg-gray-200 rounded-lg flex items-center justify-center">
                  <ImageIcon className="h-16 w-16 text-gray-400" />
                </div>
              )}
            </div>
          </div>
        );

      case 'image-top-text':
        return (
          <div style={{ backgroundColor: data.backgroundColor || '#FFFFFF' }}>
            <div style={{ height: getHeight(data.imageHeight) }} className="overflow-hidden">
              {data.image ? (
                <img src={data.image} alt={data.imageAlt || ''} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                  <ImageIcon className="h-16 w-16 text-gray-400" />
                </div>
              )}
            </div>
            <div className="p-8" style={{ textAlign: data.textAlign || 'center' }}>
              <h2 
                style={{ 
                  color: data.titleColor || '#1F2937',
                  fontWeight: data.titleBold ? 'bold' : 'normal'
                }}
                className="text-2xl md:text-3xl mb-4"
              >
                {data.title || 'Überschrift'}
              </h2>
              <p style={{ color: data.textColor || '#4B5563' }} className="text-base max-w-2xl mx-auto">
                {data.text || 'Ihr Text hier...'}
              </p>
            </div>
          </div>
        );

      case 'three-columns-icons':
        return (
          <div style={{ backgroundColor: data.backgroundColor || '#F5F0EB', padding: '3rem 1.5rem' }}>
            <h2 
              style={{ color: data.sectionTitleColor || '#1F2937' }}
              className="text-2xl md:text-3xl font-bold text-center mb-8"
            >
              {data.sectionTitle || 'Unsere Vorteile'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {[1, 2, 3].map(i => (
                <div key={i} className="text-center">
                  <span className="text-5xl mb-4 block">{data[`column${i}Icon`] || '✨'}</span>
                  <h3 
                    style={{ color: data.columnTitleColor || '#1F2937' }}
                    className="text-xl font-semibold mb-2"
                  >
                    {data[`column${i}Title`] || `Vorteil ${i}`}
                  </h3>
                  <p style={{ color: data.columnTextColor || '#6B7280' }}>
                    {data[`column${i}Text`] || 'Beschreibung...'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'full-width-banner':
        return (
          <div 
            className="relative flex items-center justify-center overflow-hidden"
            style={{ 
              height: getHeight(data.height),
              backgroundImage: data.backgroundImage ? `url(${data.backgroundImage})` : 'linear-gradient(135deg, #8B7355, #6D5A47)',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            <div 
              className="absolute inset-0"
              style={{ backgroundColor: data.overlayColor || 'rgba(0,0,0,0.5)' }}
            />
            <div 
              className="relative z-10 text-center px-4"
              style={{ 
                color: data.textColor || '#FFFFFF',
                textAlign: data.textAlign || 'center'
              }}
            >
              <h2 
                className="text-3xl md:text-5xl mb-4"
                style={{ fontWeight: data.titleBold ? 'bold' : 'normal' }}
              >
                {data.title || 'Willkommen'}
              </h2>
              <p className="text-lg md:text-xl mb-6 opacity-90">{data.subtitle || 'Untertitel'}</p>
              {data.buttonText && (
                <Button 
                  size="lg"
                  style={{ backgroundColor: data.buttonColor || '#8B7355' }}
                >
                  {data.buttonText}
                </Button>
              )}
            </div>
          </div>
        );

      case 'gallery':
        const images = [data.image1, data.image2, data.image3, data.image4, data.image5, data.image6].filter(Boolean);
        const cols = parseInt(data.columns || '3');
        return (
          <div style={{ backgroundColor: data.backgroundColor || '#FFFFFF', padding: '3rem 1.5rem' }}>
            <h2 
              style={{ color: data.sectionTitleColor || '#1F2937' }}
              className="text-2xl md:text-3xl font-bold text-center mb-8"
            >
              {data.sectionTitle || 'Galerie'}
            </h2>
            <div 
              className="max-w-6xl mx-auto"
              style={{ 
                display: 'grid',
                gridTemplateColumns: `repeat(${cols}, 1fr)`,
                gap: getGap(data.gap)
              }}
            >
              {(images.length > 0 ? images : [1, 2, 3, 4, 5, 6]).slice(0, 6).map((img, i) => (
                <div 
                  key={i} 
                  className={`aspect-square overflow-hidden ${data.rounded ? 'rounded-lg' : ''}`}
                >
                  {typeof img === 'string' && img ? (
                    <img src={img} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform" />
                  ) : (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                      <ImageIcon className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return <div className="p-8 text-center text-gray-500">Vorschau nicht verfügbar</div>;
    }
  };

  // Render small preview for card
  const renderSmallPreview = (section) => {
    const template = SECTION_TEMPLATES.find(t => t.id === section.templateId);
    if (!template) return null;

    const data = section.data || {};

    switch (section.templateId) {
      case 'text-image-right':
        return (
          <div className="flex gap-2 p-3 rounded-lg h-full" style={{ backgroundColor: data.backgroundColor || '#F5F0EB' }}>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-sm truncate" style={{ color: data.titleColor }}>{data.title || 'Überschrift'}</h3>
              <p className="text-xs text-gray-600 line-clamp-2">{data.text || 'Text...'}</p>
            </div>
            <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center flex-shrink-0 overflow-hidden">
              {data.image ? (
                <img src={data.image} alt="" className="w-full h-full object-cover" />
              ) : (
                <ImageIcon className="h-6 w-6 text-gray-400" />
              )}
            </div>
          </div>
        );

      case 'image-top-text':
        return (
          <div className="rounded-lg overflow-hidden h-full" style={{ backgroundColor: data.backgroundColor || '#FFFFFF' }}>
            <div className="h-16 bg-gray-200 flex items-center justify-center overflow-hidden">
              {data.image ? (
                <img src={data.image} alt="" className="w-full h-full object-cover" />
              ) : (
                <ImageIcon className="h-6 w-6 text-gray-400" />
              )}
            </div>
            <div className="p-2 text-center">
              <h3 className="font-bold text-sm" style={{ color: data.titleColor }}>{data.title || 'Überschrift'}</h3>
              <p className="text-xs text-gray-600 line-clamp-1">{data.text || 'Text...'}</p>
            </div>
          </div>
        );

      case 'three-columns-icons':
        return (
          <div className="p-3 rounded-lg h-full" style={{ backgroundColor: data.backgroundColor || '#F5F0EB' }}>
            <h3 className="font-bold text-sm text-center mb-2" style={{ color: data.sectionTitleColor }}>{data.sectionTitle || 'Vorteile'}</h3>
            <div className="grid grid-cols-3 gap-1 text-center text-xs">
              {[1, 2, 3].map(i => (
                <div key={i}>
                  <span className="text-lg">{data[`column${i}Icon`] || '✨'}</span>
                  <p className="font-medium truncate">{data[`column${i}Title`] || 'Titel'}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'full-width-banner':
        return (
          <div
            className="h-20 rounded-lg flex items-center justify-center relative overflow-hidden"
            style={{
              backgroundImage: data.backgroundImage ? `url(${data.backgroundImage})` : 'linear-gradient(135deg, #8B7355, #6D5A47)',
              backgroundSize: 'cover'
            }}
          >
            <div className="absolute inset-0" style={{ backgroundColor: data.overlayColor || 'rgba(0,0,0,0.5)' }} />
            <div className="relative z-10 text-center" style={{ color: data.textColor || '#FFFFFF' }}>
              <h3 className="font-bold text-sm">{data.title || 'Banner'}</h3>
              <p className="text-xs opacity-80">{data.subtitle || 'Untertitel'}</p>
            </div>
          </div>
        );

      case 'gallery':
        const images = [data.image1, data.image2, data.image3].filter(Boolean);
        return (
          <div className="p-3 rounded-lg h-full" style={{ backgroundColor: data.backgroundColor || '#FFFFFF' }}>
            <h3 className="font-bold text-sm text-center mb-2" style={{ color: data.sectionTitleColor }}>{data.sectionTitle || 'Galerie'}</h3>
            <div className="grid grid-cols-3 gap-1">
              {(images.length > 0 ? images : [1, 2, 3]).map((img, i) => (
                <div key={i} className="aspect-square bg-gray-200 rounded flex items-center justify-center overflow-hidden">
                  {typeof img === 'string' && img ? (
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="h-3 w-3 text-gray-400" />
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return <div className="p-3 bg-gray-100 rounded-lg text-center text-gray-500 text-xs">Vorschau</div>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen py-8 px-4" style={{ backgroundColor: '#E8E0D5' }}>
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 sm:py-6 lg:py-8 px-3 sm:px-4 md:px-6 lg:px-8" style={{ backgroundColor: '#E8E0D5' }}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Link to="/admin">
              <Button variant="outline" size="icon">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">Abschnitt-Vorlagen</h1>
              <p className="text-sm text-gray-600">Gestalten Sie benutzerdefinierte Seitenabschnitte</p>
            </div>
          </div>
          <Button onClick={openTemplateDialog} className="rounded-none bg-[#C4A484] hover:bg-[#B89968] text-white">
            <Plus className="h-4 w-4 mr-2" />
            Neuer Abschnitt
          </Button>
        </div>

        {/* Info Box */}
        <Card className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-4">
            <p className="text-sm text-blue-900">
              <strong>So funktioniert's:</strong> Wählen Sie eine Vorlage, passen Sie Farben und Texte an, und sehen Sie eine Live-Vorschau. 
              Die Abschnitte können per Drag & Drop sortiert werden.
            </p>
          </CardContent>
        </Card>

        {/* Sections List */}
        {sections.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Layout className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Keine Abschnitte vorhanden</h3>
              <p className="text-gray-500 mb-4">Erstellen Sie Ihren ersten Abschnitt aus einer Vorlage.</p>
              <Button onClick={openTemplateDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Ersten Abschnitt erstellen
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {sections.map((section, index) => {
              const template = SECTION_TEMPLATES.find(t => t.id === section.templateId);
              const TemplateIcon = template?.icon || Layout;

              return (
                <Card
                  key={section.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`transition-all cursor-move border-2 ${
                    draggedItem === index ? 'opacity-50 scale-95' : 'hover:shadow-lg hover:border-blue-400'
                  } ${!section.isActive ? 'opacity-60' : ''}`}
                >
                  <CardContent className="p-0">
                    <div className="flex flex-col sm:flex-row">
                      {/* Drag Handle */}
                      <div className="flex sm:flex-col items-center justify-center bg-gray-100 p-3 sm:p-4 cursor-grab active:cursor-grabbing border-b sm:border-b-0 sm:border-r">
                        <GripVertical className="h-6 w-6 text-gray-400" />
                        <span className="ml-2 sm:ml-0 sm:mt-2 font-bold text-gray-500">{index + 1}</span>
                      </div>

                      {/* Preview */}
                      <div className="w-full sm:w-72 p-3 border-b sm:border-b-0 sm:border-r bg-gray-50">
                        {renderSmallPreview(section)}
                      </div>

                      {/* Info & Controls */}
                      <div className="flex-1 p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <TemplateIcon className="h-5 w-5 text-gray-600" />
                            <h3 className="font-semibold">{template?.name || 'Unbekannte Vorlage'}</h3>
                            <Badge variant={section.isActive ? 'default' : 'secondary'}>
                              {section.isActive ? 'Aktiv' : 'Inaktiv'}
                            </Badge>
                          </div>
                        </div>
                        <p className="text-sm text-gray-500 mb-4">{template?.description}</p>

                        <div className="flex flex-wrap gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openPreview(section)}
                          >
                            <Maximize2 className="h-4 w-4 mr-1" />
                            Vorschau
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleSection(section.id)}
                          >
                            {section.isActive ? <Eye className="h-4 w-4 mr-1" /> : <X className="h-4 w-4 mr-1" />}
                            {section.isActive ? 'Aktiv' : 'Inaktiv'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => editSection(section)}
                          >
                            <Edit2 className="h-4 w-4 mr-1" />
                            Bearbeiten
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => deleteSection(section.id)}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Löschen
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Template Selection Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Vorlage wählen</DialogTitle>
            <DialogDescription>Wählen Sie eine Vorlage für Ihren neuen Abschnitt</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            {SECTION_TEMPLATES.map(template => {
              const Icon = template.icon;
              return (
                <Card
                  key={template.id}
                  className="cursor-pointer hover:border-blue-500 hover:shadow-lg transition-all"
                  onClick={() => selectTemplate(template)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#8B7355] to-[#6D5A47] flex items-center justify-center text-white">
                        <Icon className="h-6 w-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold">{template.name}</h3>
                        <p className="text-sm text-gray-500">{template.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Section Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowEditDialog(false);
          setEditingSection(null);
          setSelectedTemplate(null);
          setEditFormData({});
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingSection ? 'Abschnitt bearbeiten' : 'Neuen Abschnitt erstellen'}
            </DialogTitle>
            <DialogDescription>
              {selectedTemplate?.name} - {selectedTemplate?.description}
            </DialogDescription>
          </DialogHeader>

          {selectedTemplate && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
              {/* Form Fields */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Einstellungen</h3>
                {selectedTemplate.fields.map(field => renderField(field))}
              </div>

              {/* Live Preview */}
              <div>
                <h3 className="font-semibold text-gray-700 border-b pb-2 mb-4">Live-Vorschau</h3>
                <div className="border rounded-lg overflow-hidden bg-white">
                  {renderFullPreview({ templateId: selectedTemplate.id, data: editFormData })}
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4 border-t mt-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                setShowEditDialog(false);
                setEditingSection(null);
                setSelectedTemplate(null);
                setEditFormData({});
              }}
            >
              Abbrechen
            </Button>
            <Button
              className="flex-1 rounded-none bg-[#C4A484] hover:bg-[#B89968] text-white"
              onClick={editingSection ? updateSection : addSection}
              disabled={saving}
            >
              <Check className="h-4 w-4 mr-2" />
              {saving ? 'Speichert...' : (editingSection ? 'Aktualisieren' : 'Erstellen')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto p-0">
          <DialogHeader className="p-4 border-b">
            <DialogTitle>Vorschau: {previewSection?.templateName}</DialogTitle>
          </DialogHeader>
          <div className="bg-gray-100">
            {previewSection && renderFullPreview(previewSection)}
          </div>
          <div className="p-4 border-t flex justify-end">
            <Button onClick={() => setShowPreviewDialog(false)}>Schließen</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SectionTemplates;
